import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}/account-expand';
const HTTP_METHOD = 'get';

export type MakeGetOrderByIdentifierExpandedDeps<ODetailPaid extends Schema.Top> = {
  readonly orderDetailPaid: ODetailPaid;
};

export const makeGetOrderByIdentifierExpanded = <ODetailPaid extends Schema.Top>(
  deps: MakeGetOrderByIdentifierExpandedDeps<ODetailPaid>
) => {
  const GetOrderByIdentifierExpandedError = createBackendApiError({
    domain: 'Order',
    endpoint: 'GetByIdentifierExpanded',
    path: ENDPOINT,
    identifierField: 'tokenValue',
  } as const);

  const getOrderByIdentifierExpandedFetch = (tokenValue: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ params: { tokenValue } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'order.tokenValue': tokenValue,
        },
      })
    );

  const getOrderByIdentifierExpanded = (tokenValue: string) =>
    getOrderByIdentifierExpandedFetch(tokenValue).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.orderDetailPaid)(response),
          404: (response) =>
            Effect.fail(
              new GetOrderByIdentifierExpandedError.NotFoundError({ tokenValue, response })
            ),
          orElse: (response) =>
            Effect.fail(
              new GetOrderByIdentifierExpandedError.ApiRequestError({ tokenValue, response })
            ),
        })
      ),
      Effect.tap(() =>
        logApiOperation(
          ApiModule.OrderGetByIdentifier,
          'Debug',
          `Successfully resolved order for tokenValue: ${tokenValue}`
        )
      ),
      Effect.withSpan('api.order.getByIdentifier', {
        kind: 'internal',
        attributes: {
          'order.tokenValue': tokenValue,
        },
      })
    );

  return { getOrderByIdentifierExpanded, GetOrderByIdentifierExpandedError };
};
